/**
 * Shared date/time picker helpers.
 *
 * The API always serializes datetimes in config('app.timezone') (Asia/Kolkata)
 * with an explicit "+05:30" offset (see SerializesDatesInAppTimezone on the
 * backend). Round-tripping those values through native Date methods like
 * setHours()/toISOString()/toTimeString() silently converts using the
 * browser/OS's local system timezone instead, drifting the displayed or saved
 * time whenever that isn't IST. These helpers work on the string directly so
 * date/time pickers behave the same regardless of the client's local timezone.
 */

export const APP_TIMEZONE = "Asia/Kolkata";
const APP_UTC_OFFSET = "+05:30";

/**
 * Splits an API datetime string into { date: 'YYYY-MM-DD', time: 'HH:MM' }
 * for binding to a v-date-picker/v-time-picker pair.
 */
export function splitIstDateTime(isoString) {
  const match = isoString && /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(isoString);
  return match ? { date: match[1], time: match[2] } : { date: "", time: "" };
}

/**
 * Combines a picker's separate date/time values into an unambiguous
 * IST-offset ISO string to send back to the API.
 */
export function combineIstDateTime(date, time) {
  if (!date) return null;
  return `${date}T${time || "00:00"}:00${APP_UTC_OFFSET}`;
}

/** Current date/time as { date: 'YYYY-MM-DD', time: 'HH:MM' } in IST. */
export function nowInIst() {
  // Built from formatToParts (not a formatted string) with hourCycle: "h23" so
  // midnight always reads "00", not "24" — some ICU/locale combinations render
  // hour12:false as "24:00" instead of "00:00" for midnight.
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());

  const part = (type) => parts.find((p) => p.type === type).value;
  return {
    date: `${part("year")}-${part("month")}-${part("day")}`,
    time: `${part("hour")}:${part("minute")}`,
  };
}
